import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaCrown, FaBitcoin, FaExternalLinkAlt, FaCopy, FaHistory } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  useCheckVipEligibilityQuery,
  useGetVipRatesQuery,
  useGetVipSettingsQuery,
  useLazyGetVipAddressQuery,
  useGetVipDepositsQuery,
} from '../../features/vip-trading/vipTradingApiSlice';
import { VipCoin, VipDepositStatus } from '../../features/vip-trading/types';
import { Loader } from '../../shared/Loader';
import { useGetAllCoins24hChangesQuery } from '../../features/trade&coins/coinsChangesSlice';
import { selectCurrentUser } from '../../features/auth/authSlice';

// USDT Contract Addresses by network
const CONTRACT_ADDRESSES = {
  mainnet: {
    [VipCoin.USDT_TRC20]: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    [VipCoin.USDT_ERC20]: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  },
  testnet: {
    [VipCoin.USDT_TRC20]: 'TXLAQ63Xg1NAzckPwKHvzw7CSEmLMEqcdj', // Nile testnet USDT
    [VipCoin.USDT_ERC20]: '0x7169D38820dfd117C3FA1f22a697dBA58d90BA06', // Sepolia testnet USDT
  },
};

const coinConfig = {
  [VipCoin.BITCOIN]: {
    name: 'Bitcoin',
    symbol: 'BTC',
    network: 'Bitcoin Network',
    apiCoinName: 'bitcoin',
    icon: FaBitcoin,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
  },
  [VipCoin.USDT_TRC20]: {
    name: 'USDT (TRC20)',
    symbol: 'USDT',
    network: 'TRON Network (TRC20)',
    apiCoinName: 'usdt',
    icon: SiTether,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
  },
  [VipCoin.USDT_ERC20]: {
    name: 'USDT (ERC20)',
    symbol: 'USDT',
    network: 'Ethereum Network (ERC20)',
    apiCoinName: 'usdt',
    icon: SiTether,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
  },
};

const statusColors: Record<VipDepositStatus, string> = {
  [VipDepositStatus.PENDING]: 'bg-yellow-500/20 text-yellow-400',
  [VipDepositStatus.PROCESSING]: 'bg-blue-500/20 text-blue-400',
  [VipDepositStatus.PENDING_REVIEW]: 'bg-orange-500/20 text-orange-400',
  [VipDepositStatus.CONFIRMED]: 'bg-cyan-500/20 text-cyan-400',
  [VipDepositStatus.COMPLETED]: 'bg-green-500/20 text-green-400',
  [VipDepositStatus.REJECTED]: 'bg-red-500/20 text-red-400',
};

export const VipTrading = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [selectedCoin, setSelectedCoin] = useState<VipCoin | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data: eligibility, isLoading: eligibilityLoading } = useCheckVipEligibilityQuery();
  const { data: rates, isLoading: ratesLoading } = useGetVipRatesQuery();
  const { data: settings, isLoading: settingsLoading } = useGetVipSettingsQuery();
  const { data: deposits, isLoading: depositsLoading } = useGetVipDepositsQuery();
  const { data: cryptoPrices } = useGetAllCoins24hChangesQuery();
  const [getAddress, { data: addressData, isLoading: addressLoading }] = useLazyGetVipAddressQuery();

  const isLoading = eligibilityLoading || ratesLoading || settingsLoading;

  // Get live crypto price
  const getCryptoPrice = (coin: VipCoin) => {
    const config = coinConfig[coin];
    if (!cryptoPrices || !cryptoPrices[config.apiCoinName]) return null;
    return cryptoPrices[config.apiCoinName].usd;
  };

  // Get contract address based on network
  const getContractAddress = (coin: VipCoin) => {
    const network = settings?.network || 'mainnet';
    const networkContracts = CONTRACT_ADDRESSES[network];
    return networkContracts?.[coin as keyof typeof networkContracts];
  };

  // Copy contract address
  const copyContractAddress = (coin: VipCoin) => {
    const contract = getContractAddress(coin);
    if (contract) {
      navigator.clipboard.writeText(contract);
      toast.success('Contract address copied');
    }
  };

  const handleCoinSelect = async (coin: VipCoin) => {
    setSelectedCoin(coin);
    await getAddress(coin);
  };

  const copyAddress = () => {
    if (addressData?.address) {
      navigator.clipboard.writeText(addressData.address);
      toast.success('Address copied to clipboard');
    }
  };

  const formatAmount = (amount: number, decimals = 2) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Check eligibility
  if (!eligibility?.eligible) {
    return (
      <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
        <Helmet>
          <title>VIP Trading | GCBuying</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-6 py-12">
          <div className="text-center">
            <FaCrown className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-4">VIP Trading Access Required</h1>
            <p className="text-gray-400 mb-8">
              {eligibility?.reason === 'KYC_NOT_VERIFIED'
                ? 'Complete KYC verification to access VIP Trading with premium rates.'
                : 'VIP Trading is currently unavailable. Please try again later.'}
            </p>
            {eligibility?.reason === 'KYC_NOT_VERIFIED' && (
              <button
                onClick={() => navigate('/app/account/kyc')}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Complete KYC Verification
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
      <Helmet>
        <title>VIP Trading | GCBuying</title>
        <meta name="description" content="Sell crypto at premium VIP rates" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20">
              <FaCrown className="w-8 h-8 text-yellow-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                VIP Trading
                {user?.badges?.includes('VIP_VERIFIED') && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full font-medium">
                    <FaCrown className="w-3 h-3" />
                    VIP Verified
                  </span>
                )}
              </h1>
              <p className="text-gray-400">Sell crypto at premium rates</p>
            </div>
          </div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showHistory ? 'bg-primary text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            <FaHistory />
            History
          </button>
        </div>

        {showHistory ? (
          // History Section
          <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-6">
            <h2 className="text-xl font-semibold text-white mb-6">VIP Transaction History</h2>
            {depositsLoading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : deposits?.data?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400">No VIP transactions yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {deposits?.data?.map((deposit) => {
                  const config = coinConfig[deposit.coin];
                  const Icon = config.icon;
                  return (
                    <div
                      key={deposit._id}
                      className="flex items-center justify-between p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${config.bgColor}`}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {deposit.amount} {config.symbol}
                          </p>
                          <p className="text-sm text-gray-400">
                            ${formatAmount(deposit.amountInUsd || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs ${statusColors[deposit.status]}`}>
                          {deposit.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {deposit.amountInNGN && (
                          <p className="text-green-400 font-medium mt-1">
                            +₦{formatAmount(deposit.amountInNGN)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Info Banner */}
            <div className="mb-8 p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30">
              <div className="flex items-start gap-4">
                <FaCrown className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-medium mb-1">Premium VIP Rates</h3>
                  <p className="text-gray-400 text-sm">
                    Minimum deposit: <span className="text-yellow-400 font-medium">${settings?.minimumDepositUsd?.toLocaleString()}</span>
                    {' '}| Deposits below the stipulated minimum will be manually reviewed and credited at a significantly reduced rate. Users are advised to strictly observe the minimum requirement.
                  </p>
                </div>
              </div>
            </div>

            {/* Coin Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Object.entries(coinConfig).map(([coin, config]) => {
                const Icon = config.icon;
                const rate = rates?.[coin as VipCoin];
                const cryptoPrice = getCryptoPrice(coin as VipCoin);
                return (
                  <button
                    key={coin}
                    onClick={() => handleCoinSelect(coin as VipCoin)}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      selectedCoin === coin
                        ? `${config.borderColor} ${config.bgColor}`
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className={`w-8 h-8 ${config.color}`} />
                      <div className="text-left">
                        <p className="text-white font-medium">{config.name}</p>
                        <p className="text-sm text-gray-400">{config.network}</p>
                      </div>
                    </div>
                    <div className="text-left mb-2">
                      <p className="text-xs text-white/40">Live Price</p>
                      {cryptoPrice ? (
                        <p className="text-sm font-medium text-white/80">
                          1 {config.symbol} = ${formatAmount(cryptoPrice)}
                        </p>
                      ) : (
                        <p className="text-sm text-white/40">—</p>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-white/40">VIP Rate</p>
                      {rate ? (
                        <p className="text-lg font-semibold text-yellow-400">
                          ₦{formatAmount(rate.ngn)} / $1
                        </p>
                      ) : ratesLoading ? (
                        <p className="text-sm text-white/40">Loading...</p>
                      ) : (
                        <p className="text-sm text-white/40">—</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Address Display */}
            {selectedCoin && (
              <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-6">
                {addressLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader />
                  </div>
                ) : addressData?.address ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Your VIP {coinConfig[selectedCoin].name} Address
                      </h3>
                      <p className="text-gray-400 text-sm">
                        Send only {coinConfig[selectedCoin].name} to this address
                      </p>
                      {settings?.network === 'testnet' && (
                        <span className="inline-block mt-2 px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full">
                          ⚠️ TESTNET MODE
                        </span>
                      )}
                    </div>

                    <div className="flex justify-center">
                      <div className="p-4 bg-white rounded-xl">
                        <QRCodeSVG value={addressData.address} size={180} />
                      </div>
                    </div>

                    {/* YOUR DEPOSIT ADDRESS - Primary action */}
                    <div className="relative p-5 rounded-2xl bg-gradient-to-br from-primary/20 via-purple-500/10 to-primary/5 border border-primary/30 backdrop-blur-sm">
                      <div className="absolute top-0 right-0 px-3 py-1 bg-primary text-white text-xs font-bold rounded-bl-xl rounded-tr-xl">
                        DEPOSIT HERE
                      </div>
                      <p className="text-sm font-medium text-white/80 mb-3 mt-2">Your {coinConfig[selectedCoin].symbol} Deposit Address</p>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={addressData.address}
                          className="w-full px-4 py-4 pr-14 bg-black/40 border border-white/10 rounded-xl text-white text-sm font-mono tracking-wide"
                        />
                        <button
                          onClick={copyAddress}
                          className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-primary hover:bg-primary/80 rounded-lg transition-colors"
                        >
                          <FaCopy className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      <p className="text-xs text-white/50 mt-3">
                        Send your {coinConfig[selectedCoin].name} to this address
                      </p>
                    </div>

                    {/* Contract Address for USDT - Reference info */}
                    {getContractAddress(selectedCoin) && (
                      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-white/40 uppercase tracking-wider">
                            Token Contract {settings?.network === 'testnet' && '(Testnet)'}
                          </p>
                          <button
                            onClick={() => copyContractAddress(selectedCoin)}
                            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <FaCopy className="w-3 h-3 text-white/40" />
                          </button>
                        </div>
                        <p className="text-sm font-mono text-white/70 break-all">
                          {getContractAddress(selectedCoin)}
                        </p>
                        <p className="text-xs text-white/30 mt-2">
                          For verification only — do not send funds to this address
                        </p>
                      </div>
                    )}

                    {/* Live Rate Display */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wider">Live Price</p>
                          <p className="text-lg font-semibold text-white">
                            {getCryptoPrice(selectedCoin)
                              ? `1 ${coinConfig[selectedCoin].symbol} = $${formatAmount(getCryptoPrice(selectedCoin) || 0)}`
                              : '—'
                            }
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-white/50 uppercase tracking-wider">VIP Rate</p>
                          <p className="text-lg font-semibold text-yellow-400">
                            {rates?.[selectedCoin]
                              ? `₦${formatAmount(rates[selectedCoin].ngn)} / $1`
                              : '—'
                            }
                          </p>
                        </div>
                      </div>
                      {getCryptoPrice(selectedCoin) && rates?.[selectedCoin] && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-xs text-white/40">Estimated Value</p>
                          <p className="text-sm font-medium text-white/80">
                            1 {coinConfig[selectedCoin].symbol} ≈ ₦{formatAmount((getCryptoPrice(selectedCoin) || 0) * rates[selectedCoin].ngn)}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                      <h4 className="text-yellow-400 font-medium mb-2">Important</h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• Minimum VIP deposit: ${settings?.minimumDepositUsd?.toLocaleString()}</li>
                        <li>• Deposits below minimum will require manual review</li>
                        <li>• Only send {coinConfig[selectedCoin].name} to this address</li>
                        <li>• Funds will be credited after confirmations</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">Failed to generate address. Please try again.</p>
                  </div>
                )}
              </div>
            )}

            {/* Support Link */}
            {settings?.telegramSupportLink && (
              <div className="mt-8 text-center">
                <a
                  href={settings.telegramSupportLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 rounded-xl text-primary hover:bg-primary/30 transition-colors"
                >
                  <FaCrown className="w-5 h-5" />
                  VIP Priority Support
                  <FaExternalLinkAlt className="w-3 h-3" />
                </a>
                <p className="text-white/60 text-sm mt-2">
                  Dedicated VIP agents at your service
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VipTrading;
